<template>
  <div
    class="plan-card"
    :class="{
      'plan-card--featured': plan.is_featured,
      'plan-card--current': isCurrent,
      'plan-card--checkout': checkout
    }"
  >
    <div v-if="plan.is_featured" class="plan-card__badge">Recomendado</div>

    <p class="plan-card__name">{{ plan.display_name || plan.name }}</p>
    <p class="plan-card__tagline">{{ plan.tagline }}</p>

    <!-- Pricing -->
    <div class="plan-card__pricing">
      <div class="plan-card__price-row">
        <span class="plan-card__amount">${{ displayPrice }}</span>
        <span class="plan-card__period">/mes</span>
        <span v-if="billingPeriod === 'monthly'" class="plan-card__iva"
          >+ IVA</span
        >
      </div>

      <!-- Promo price (strikethrough original) -->
      <template v-if="pricing && pricing.promo_price">
        <p class="plan-card__promo">
          <span class="plan-card__original-price">${{ pricing.price }}</span>
          <span class="plan-card__promo-price">${{ pricing.promo_price }}</span>
        </p>
      </template>

      <!-- Annual details -->
      <p
        v-if="billingPeriod === 'annual' && pricing"
        class="plan-card__annual-detail"
      >
        <span class="plan-card__annual-price"
          >${{ pricing.price }}/a&ntilde;o</span
        >
        <span class="plan-card__annual-savings" v-if="pricing.annual_savings">
          + IVA (ahorras ${{ pricing.annual_savings }})
        </span>
      </p>
      <p v-if="billingPeriod === 'monthly'" class="plan-card__spacer">&nbsp;</p>
    </div>

    <!-- CTA Button — hidden in checkout context -->
    <button
      v-if="!checkout"
      class="plan-card__cta"
      :class="{
        'plan-card__cta--featured': plan.is_featured,
        'plan-card__cta--current': isCurrent,
        'plan-card__cta--downgrade': isDowngrade
      }"
      :disabled="isCurrent"
      @click="handleAction"
    >
      {{ ctaLabel }}
    </button>

    <!-- Features list -->
    <div class="plan-card__features">
      <div
        v-for="(feat, idx) in displayFeatures"
        :key="idx"
        class="plan-card__feature"
        :class="{
          'plan-card__feature--disabled': !feat.isSep && !feat.included,
          'plan-card__feature--sep': feat.isSep
        }"
      >
        <template v-if="feat.isSep">
          <span class="plan-card__feature-sep-text">{{ feat.text }}</span>
        </template>
        <template v-else>
          <svg
            v-if="feat.included"
            class="plan-card__check"
            width="15"
            height="15"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M3 8.5L6.5 12L13 4"
              stroke="#1D9E75"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <svg
            v-else
            class="plan-card__x"
            width="15"
            height="15"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M4 4L12 12M12 4L4 12"
              stroke="#ccc"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
          <span
            class="plan-card__feature-text"
            :class="{ 'plan-card__feature-text--highlight': feat.highlight }"
          >
            {{ feat.text }}
          </span>
        </template>
      </div>
    </div>

    <!-- Firma toggle (annual only, all plans) -->
    <div
      v-if="billingPeriod === 'annual' && !checkout"
      class="plan-card__firma"
    >
      <label class="plan-card__firma-label"
        >Agregar firma electr&oacute;nica</label
      >
      <el-switch
        v-model="firmaLocal"
        active-color="#3ecfb4"
        inactive-color="#989aae"
        @change="onFirmaChange"
      />
      <p class="plan-card__firma-price">
        + $24 / mes + IVA
      </p>
    </div>

    <!-- Módulo de marketing (WhatsApp) — checkbox por columna, se suma al
         MISMO cobro del plan (un solo total). Se mantiene visible aunque ya
         esté activo (para que se vea al comparar/subir de plan), pero en ese
         caso se muestra bloqueado/informativo — nunca se vuelve a sumar al
         precio ni se re-dispara la activación. -->
    <div
      v-if="showMarketingAddonToggle"
      class="plan-card__firma plan-card__marketing-addon"
    >
      <template v-if="marketingAddonActive">
        <label class="plan-card__firma-label"
          >M&oacute;dulo de marketing (WhatsApp)</label
        >
        <el-switch :value="true" disabled active-color="#3ecfb4" />
        <p class="plan-card__firma-price">
          Ya lo tienes activo — se mantiene igual al cambiar de plan
        </p>
      </template>
      <template v-else>
        <label class="plan-card__firma-label"
          >Agregar m&oacute;dulo de marketing (WhatsApp)</label
        >
        <el-switch
          v-model="marketingAddonLocal"
          active-color="#3ecfb4"
          inactive-color="#989aae"
          @change="onMarketingAddonChange"
        />
        <p class="plan-card__firma-price">
          + ${{ marketingAddonPriceForPeriod }} /
          {{ billingPeriod === "annual" ? "año" : "mes" }} + IVA &mdash; incluye
          ${{ marketingAddonPlan.welcome_gift_usd }} de saldo de bienvenida
        </p>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: "NewPlanCard",
  props: {
    plan: { type: Object, required: true },
    checkout: { type: Boolean, default: false },
    marketingAddonPlan: { type: Object, default: null },
    marketingAddonActive: { type: Boolean, default: false }
  },
  data() {
    return {
      firmaLocal: false,
      marketingAddonLocal: false
    };
  },
  computed: {
    billingPeriod() {
      return this.$store.getters.billingPeriod;
    },
    pricing() {
      return this.plan.pricing?.[this.billingPeriod] ?? null;
    },
    marketingAddonPriceForPeriod() {
      if (!this.marketingAddonPlan) return 0;
      return this.billingPeriod === "annual"
        ? this.marketingAddonPlan.price_annual
        : this.marketingAddonPlan.price_monthly;
    },
    showMarketingAddonToggle() {
      return !this.checkout && !!this.marketingAddonPlan;
    },
    displayPrice() {
      if (!this.pricing) return "—";
      const base =
        this.pricing.monthly_equivalent ?? this.pricing.effective_price;
      const firma = this.firmaLocal && this.billingPeriod === "annual" ? 24 : 0;
      const addon = this.marketingAddonLocal
        ? this.marketingAddonPriceForPeriod /
          (this.billingPeriod === "annual" ? 12 : 1)
        : 0;
      return Math.round(base) + firma + Math.round(addon);
    },
    isCurrent() {
      return this.$store.getters.isCurrentPlan(this.plan.slug);
    },
    isDowngrade() {
      const userPlan = this.$store.getters.userPlan;
      if (!userPlan) return false;
      return this.plan.sort_order < (this.currentPlanSortOrder || 0);
    },
    currentPlanSortOrder() {
      const userPlan = this.$store.getters.userPlan;
      if (!userPlan) return 0;
      const currentSlug = userPlan.plan?.name;
      const currentPlan = this.$store.getters.plans.find(
        p => p.slug === currentSlug
      );
      return currentPlan?.sort_order ?? 0;
    },
    ctaLabel() {
      if (this.isCurrent) return "Tu plan";
      if (this.isDowngrade) return "Cambiar";
      if (this.$store.getters.userPlan) return "Mejorar";
      return "Empezar ahora";
    },
    showFirmaToggle() {
      // Only show if the plan has electronic_signature_available = true
      const features = this.plan.features;
      if (!features) return false;
      const facturacion = features.facturacion || [];
      const sigFeature = facturacion.find(
        f => f.slug === "electronic_signature_available"
      );
      return sigFeature && sigFeature.value === "true";
    },
    displayFeatures() {
      return this.buildFeatureList();
    }
  },
  methods: {
    handleAction() {
      if (this.isCurrent) return;
      this.$store.dispatch("setSelectedPlan", this.plan.slug);
      this.$store.dispatch("setFirma", this.firmaLocal);
      this.$store.dispatch("setMarketingAddon", this.marketingAddonLocal);
      this.$emit("select", this.plan);
    },
    onFirmaChange(val) {
      this.$store.dispatch("setFirma", val);
    },
    onMarketingAddonChange(val) {
      this.$store.dispatch("setMarketingAddon", val);
    },
    buildFeatureList() {
      const features = this.plan.features;
      const slug = this.plan.slug;
      if (!features || typeof features !== "object") return [];

      // Helpers
      const g = (group, key) =>
        (features[group] || []).find(f => f.slug === key);
      const isUnlim = v => v === "unlimited" || v === "-1";

      // Dynamic values from API
      const docs = g("facturacion", "documents_monthly_limit");
      const users = g("plataforma", "users_limit");
      const emailC = g("email", "email_campaigns_monthly");
      const emailCt = g("email", "email_campaign_contacts");
      const signing = g("firma", "document_signing_monthly_limit");
      const ai = g("ia", "ai_credits_monthly");

      const docsText = docs
        ? isUnlim(docs.value)
          ? "Documentos electr\u00f3nicos ilimitados"
          : `${Number(docs.value).toLocaleString()} documentos / mes`
        : null;

      const usersText = users
        ? isUnlim(users.value)
          ? "Usuarios ilimitados"
          : `${users.value} usuario${users.value !== "1" ? "s" : ""}`
        : null;

      const emailText = (() => {
        if (!emailC) return null;
        const c = isUnlim(emailC.value)
          ? "ilimitadas"
          : `${emailC.value} campa\u00f1a${
              emailC.value !== "1" ? "s" : ""
            } / mes`;
        const ct =
          emailCt && !isUnlim(emailCt.value)
            ? `, ${emailCt.value} contactos c/u`
            : "";
        return `Email marketing \u2014 ${c}${ct}`;
      })();

      const signingText = signing
        ? isUnlim(signing.value)
          ? "Firma de documentos ilimitada"
          : `Firma de documentos \u2014 ${signing.value} / mes`
        : null;

      const aiText = ai
        ? isUnlim(ai.value)
          ? "Cr\u00e9ditos IA ilimitados"
          : `${ai.value} cr\u00e9ditos IA / mes`
        : null;

      // ── LANZA — plan base, lista completa ──────────────────
      if (slug === "lanza") {
        return [
          docsText && { text: docsText, included: true, highlight: true },
          { text: "Cotizaciones ilimitadas", included: true },
          usersText && { text: usersText, included: true },
          { text: "Facturaci\u00f3n electr\u00f3nica SRI", included: true },
          { text: "Cobro con link instant\u00e1neo", included: true },
          { text: "Recordatorios de cobro autom\u00e1ticos", included: true },
          { text: "Dashboard de m\u00e9tricas clave", included: true },
          { text: "Directorio de clientes", included: true },
          emailText && { text: emailText, included: true },
          signingText && { text: signingText, included: true },
          aiText && { text: aiText, included: true }
        ].filter(Boolean);
      }

      // ── CRECE — todo Lanza + lo nuevo ──────────────────────
      if (slug === "crece") {
        return [
          { isSep: true, text: "Todo de Lanza, m\u00e1s:" },
          docsText && { text: docsText, included: true, highlight: true },
          usersText && { text: usersText, included: true },
          {
            text: "Inteligencia de negocio completa",
            included: true
          },
          { text: "M\u00f3dulo de proyectos y rentabilidad", included: true },
          signingText && { text: signingText, included: true },
          aiText && { text: aiText, included: true },
          emailText && { text: emailText, included: true }
        ].filter(Boolean);
      }

      // ── ESCALA — todo Crece + lo nuevo ─────────────────────
      if (slug === "escala") {
        return [
          { isSep: true, text: "Todo de Crece, m\u00e1s:" },
          docsText && { text: docsText, included: true, highlight: true },
          usersText && { text: usersText, included: true },
          signingText && { text: signingText, included: true },
          aiText && { text: aiText, included: true },
          emailText && { text: emailText, included: true },
          { text: "Reportes exportables PDF / CSV", included: true },
          { text: "Inventario y tienda en l\u00ednea", included: true },
          { text: "API para integraciones", included: true },
          { text: "Soporte prioritario", included: true }
        ].filter(Boolean);
      }

      // ── CUSTOM / A tu medida ────────────────────────────────
      return [
        { isSep: true, text: "Todo ilimitado, m\u00e1s:" },
        {
          text: "Facturas, usuarios y campa\u00f1as sin l\u00edmite",
          included: true,
          highlight: true
        },
        { text: "Firma de documentos ilimitada", included: true },
        { text: "Cr\u00e9ditos IA ilimitados", included: true },
        { text: "Inventario y tienda en l\u00ednea", included: true },
        { text: "API para integraciones", included: true },
        { text: "Soporte dedicado 1:1", included: true }
      ];
    }
  }
};
</script>

<style lang="scss" scoped>
.plan-card {
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e8e7e3;
  padding: 24px 20px 22px;
  position: relative;
  display: flex;
  flex-direction: column;

  &--featured {
    border: 2px solid #3ecfb4;
    padding: 28px 20px 22px;
  }

  &__badge {
    position: absolute;
    top: -12px;
    left: 50%;
    transform: translateX(-50%);
    background: #3ecfb4;
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    padding: 4px 16px;
    border-radius: 20px;
    white-space: nowrap;
  }

  &__name {
    font-size: 20px;
    font-weight: 700;
    color: #2d2b55;
    margin: 0 0 4px;
  }

  &__tagline {
    font-size: 13px;
    color: #888780;
    margin: 0 0 18px;
    line-height: 1.5;
    min-height: 40px;
  }

  &__pricing {
    margin-bottom: 18px;
    min-height: 56px;
  }

  &__price-row {
    display: flex;
    align-items: baseline;
    gap: 2px;
  }

  &__amount {
    font-size: 34px;
    font-weight: 700;
    color: #2d2b55;
  }

  &__period {
    font-size: 13px;
    color: #888780;
    margin-left: 2px;
  }

  &__iva {
    font-size: 12px;
    color: #b4b2a9;
    margin-left: 6px;
  }

  &__promo {
    font-size: 13px;
    margin: 4px 0 0;
  }

  &__original-price {
    text-decoration: line-through;
    color: #b4b2a9;
    margin-right: 6px;
  }

  &__promo-price {
    color: #1d9e75;
    font-weight: 600;
  }

  &__annual-detail {
    font-size: 12px;
    margin: 5px 0 0;
  }

  &__annual-price {
    color: #1d9e75;
    font-weight: 600;
  }

  &__annual-savings {
    color: #b4b2a9;
  }

  &__spacer {
    font-size: 12px;
    color: #b4b2a9;
    margin: 4px 0 0;
  }

  &__cta {
    width: 100%;
    padding: 12px 0;
    border-radius: 9px;
    border: 1.5px solid #e8e7e3;
    background: #fff;
    color: #2d2b55;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    margin-bottom: 20px;
    transition: all 0.15s;
    font-family: "Plus Jakarta Sans", sans-serif;

    &:hover:not(:disabled) {
      border-color: #3ecfb4;
      color: #3ecfb4;
    }

    &--featured {
      border: none;
      background: #3ecfb4;
      color: #fff;

      &:hover:not(:disabled) {
        background: #1d9e75;
        color: #fff;
      }
    }

    &--current {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &--downgrade {
      border: none;
      background: transparent;
      color: #888780;
      text-decoration: underline;
      font-weight: 500;
      padding: 8px 0;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__features {
    border-top: 1px solid #f0efe8;
    padding-top: 16px;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 9px;
  }

  &__feature {
    display: flex;
    align-items: flex-start;
    gap: 8px;

    &--disabled {
      opacity: 0.45;
    }

    &--sep {
      display: block;
    }
  }

  &__check,
  &__x {
    flex-shrink: 0;
    margin-top: 2px;
  }

  &__feature-text {
    font-size: 13px;
    color: #444441;
    line-height: 1.35;

    &--highlight {
      font-weight: 600;
    }
  }

  &__feature-sep-text {
    width: 100%;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #3ecfb4;
    padding-top: 4px;
    margin-bottom: -2px;
  }

  &__firma {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #f0efe8;
    text-align: center;

    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  &__firma-label {
    font-size: 13px;
    color: #2d2b55;
    font-weight: 500;
    margin: 0;
  }

  &__firma-price {
    width: 100%;
    font-size: 11px;
    color: #b4b2a9;
    margin: 0;
  }

  // Checkout compact mode — mobile only
  &--checkout {
    @media (max-width: 767px) {
      padding: 16px;
      border-radius: 10px;
      flex-direction: row;
      align-items: center;
      flex-wrap: wrap;
      gap: 0;

      .plan-card__badge {
        display: none;
      }

      .plan-card__name {
        font-size: 16px;
        margin: 0;
        flex: 1 1 100%;
      }

      .plan-card__tagline {
        font-size: 12px;
        min-height: unset;
        margin: 2px 0 8px;
        flex: 1 1 100%;
      }

      .plan-card__pricing {
        margin-bottom: 0;
        min-height: unset;
        flex: 1 1 auto;
      }

      .plan-card__amount {
        font-size: 22px;
      }

      .plan-card__features,
      .plan-card__firma,
      .plan-card__spacer {
        display: none;
      }
    }
  }
}
</style>
